import React from 'react';
import { render, cleanup, act, fireEvent } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

async function renderComponent(element, assertions, waitMs = 200) {
  const logCapture = createLogCapture();
  let snapshot = null;
  try {
    let result;
    await act(async () => { result = render(element); });
    await act(async () => { await new Promise(r => setTimeout(r, waitMs)); });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_CODE = {
  uuid: 'code-uuid-1',
  name: 'Interview Themes',
  description: 'Main themes extracted from interviews',
  color: '#aeea84',
  excerpts: [{ id: 1 }, { id: 2 }],
  parent: null,
};

const MOCK_CODE_2 = {
  uuid: 'code-uuid-2',
  name: 'Participant Experiences',
  description: 'Personal accounts from participants',
  color: '#8cd2ef',
  excerpts: [{ id: 3 }],
  parent: null,
};

const MOCK_CHILD_CODE = {
  uuid: 'child-uuid-1',
  name: 'Subtheme A',
  description: 'A subtheme of Interview Themes',
  color: '#f4bff8',
  excerpts: [],
  parent: 'code-uuid-1',
};

const mockT = (key) => key;

// ─── TextCodeModal ────────────────────────────────────────────────────────────

export const textCoderTests = [

  {
    id: 'text-code-modal-create-mode',
    name: 'TextCodeModal: create mode renders all form fields',
    run: async () => {
      const TextCodeModal = require('../../Components/TextCoder/TextCodeModal').default;
      return await renderComponent(
        withWrapper(
          <TextCodeModal
            handleClose={() => {}}
            handleAddNewCode={() => {}}
          />,
          '/project/p-test-1/text-coder'
        ),
        async () => {
          assert(document.body.querySelector('[data-testid="text-code-modal"]') !== null, 'text-code-modal must render');
          assert(document.body.querySelector('[data-testid="text-code-name-input"]') !== null, 'name input must render');
          assert(document.body.querySelector('[data-testid="text-code-description-input"]') !== null, 'description textarea must render');
          assert(document.body.querySelector('[data-testid="text-code-save-button"]') !== null, 'save button must render');
          assert(document.body.querySelector('[data-testid="text-code-cancel-button"]') !== null, 'cancel button must render');
        }
      );
    },
  },

  {
    id: 'text-code-modal-edit-mode',
    name: 'TextCodeModal: edit mode pre-fills name and description',
    run: async () => {
      const TextCodeModal = require('../../Components/TextCoder/TextCodeModal').default;
      return await renderComponent(
        withWrapper(
          <TextCodeModal
            handleClose={() => {}}
            handleUpdateCode={() => {}}
            code={MOCK_CODE}
          />,
          '/project/p-test-1/text-coder'
        ),
        async () => {
          const nameInput = document.body.querySelector('[data-testid="text-code-name-input"]');
          assert(nameInput !== null, 'name input must render in edit mode');
          assert(
            nameInput.value === MOCK_CODE.name,
            `name input must be pre-filled with "${MOCK_CODE.name}", got "${nameInput.value}"`
          );
          const descInput = document.body.querySelector('[data-testid="text-code-description-input"]');
          assert(descInput !== null, 'description textarea must render in edit mode');
          assert(
            descInput.value === MOCK_CODE.description,
            `description must be pre-filled with "${MOCK_CODE.description}", got "${descInput.value}"`
          );
        }
      );
    },
  },

  // ─── TextCodeCombinationModal ─────────────────────────────────────────────────

  {
    id: 'text-code-combination-modal-renders',
    name: 'TextCodeCombinationModal: renders dialog with code selects',
    run: async () => {
      const TextCodeCombinationModal = require('../../Components/TextCoder/TextCodeCombinationModal').default;
      return await renderComponent(
        withWrapper(
          <TextCodeCombinationModal
            handleClose={() => {}}
            codes={[MOCK_CODE, MOCK_CODE_2]}
            handleCombineCodes={() => {}}
          />,
          '/project/p-test-1/text-coder'
        ),
        async () => {
          assert(document.body.querySelector('[data-testid="text-code-combination-modal"]') !== null, 'text-code-combination-modal must render');
          assert(document.body.querySelector('#code1') !== null, 'code1 select must render');
          assert(document.body.querySelector('#code2') !== null, 'code2 select must render');
        }
      );
    },
  },

  {
    id: 'text-code-combination-modal-lists-codes',
    name: 'TextCodeCombinationModal: lists available codes as options',
    run: async () => {
      const TextCodeCombinationModal = require('../../Components/TextCoder/TextCodeCombinationModal').default;
      return await renderComponent(
        withWrapper(
          <TextCodeCombinationModal
            handleClose={() => {}}
            codes={[MOCK_CODE, MOCK_CODE_2]}
            handleCombineCodes={() => {}}
          />,
          '/project/p-test-1/text-coder'
        ),
        async () => {
          const bodyText = document.body.textContent;
          assert(bodyText.includes(MOCK_CODE.name), `must list "${MOCK_CODE.name}" as an option`);
          assert(bodyText.includes(MOCK_CODE_2.name), `must list "${MOCK_CODE_2.name}" as an option`);
        }
      );
    },
  },

  // ─── CodeCard ────────────────────────────────────────────────────────────────

  {
    id: 'code-card-renders',
    name: 'CodeCard: renders name, description, and excerpt count',
    run: async () => {
      const CodeCard = require('../../Components/TextCoder/CodeCard').default;
      return await renderComponent(
        withWrapper(
          <CodeCard
            code={MOCK_CODE}
            textCodes={[MOCK_CODE]}
            setUploadAudioModal={() => {}}
            handleUpdateCode={() => {}}
            handleAddNewCode={() => {}}
            t={mockT}
          />,
          '/project/p-test-1/codebook'
        ),
        async ({ container }) => {
          assert(container.querySelector(`[data-testid="code-card-${MOCK_CODE.uuid}"]`) !== null, 'code-card root must render');
          assert(container.textContent.includes(MOCK_CODE.name), `must show code name "${MOCK_CODE.name}"`);
          assert(container.textContent.includes(MOCK_CODE.description), `must show description`);
          const excerptBadge = container.querySelector(`[data-testid="code-card-excerpt-count-${MOCK_CODE.uuid}"]`);
          assert(excerptBadge !== null, 'excerpt count badge must render');
          assert(
            excerptBadge.textContent.trim() === String(MOCK_CODE.excerpts.length),
            `excerpt count must be ${MOCK_CODE.excerpts.length}, got "${excerptBadge.textContent.trim()}"`
          );
        }
      );
    },
  },

  {
    id: 'code-card-shows-children',
    name: 'CodeCard: renders child code section when parent has children',
    run: async () => {
      const CodeCard = require('../../Components/TextCoder/CodeCard').default;
      return await renderComponent(
        withWrapper(
          <CodeCard
            code={MOCK_CODE}
            textCodes={[MOCK_CODE, MOCK_CHILD_CODE]}
            setUploadAudioModal={() => {}}
            handleUpdateCode={() => {}}
            handleAddNewCode={() => {}}
            t={mockT}
          />,
          '/project/p-test-1/codebook'
        ),
        async ({ container }) => {
          assert(
            container.querySelector(`[data-testid="code-card-children-${MOCK_CODE.uuid}"]`) !== null,
            'code-card-children section must render'
          );
          assert(
            container.querySelector(`[data-testid="child-code-card-${MOCK_CHILD_CODE.uuid}"]`) !== null,
            'child code card must render inside children section'
          );
          assert(container.textContent.includes(MOCK_CHILD_CODE.name), `must show child code name "${MOCK_CHILD_CODE.name}"`);
        }
      );
    },
  },

  {
    id: 'code-card-edit-opens-modal',
    name: 'CodeCard: clicking Edit opens TextCodeModal',
    run: async () => {
      const CodeCard = require('../../Components/TextCoder/CodeCard').default;

      function Wrapper() {
        const [modal, setModal] = React.useState(null);
        return (
          <>
            <CodeCard
              code={MOCK_CODE}
              textCodes={[MOCK_CODE]}
              setUploadAudioModal={setModal}
              handleUpdateCode={() => {}}
              handleAddNewCode={() => {}}
              t={mockT}
            />
            {modal}
          </>
        );
      }

      const logCapture = createLogCapture();
      let snapshot = null;
      try {
        let result;
        await act(async () => { result = render(withWrapper(<Wrapper />, '/project/p-test-1/codebook')); });
        await act(async () => { await new Promise(r => setTimeout(r, 200)); });

        const editBtn = result.container.querySelector(`[data-testid="code-card-edit-btn-${MOCK_CODE.uuid}"]`);
        assert(editBtn !== null, 'edit button must render before click');

        await act(async () => { fireEvent.click(editBtn); });
        await act(async () => { await new Promise(r => setTimeout(r, 200)); });

        assert(document.body.querySelector('[data-testid="text-code-modal"]') !== null, 'TextCodeModal must open after clicking Edit');
        snapshot = captureComponentSnapshot(result.container);
      } finally {
        cleanup();
        logCapture.restore();
      }
      return { snapshot, logs: logCapture.getLogs() };
    },
  },

  // ─── Codebook ─────────────────────────────────────────────────────────────────

  {
    id: 'codebook-empty-state',
    name: 'Codebook: renders empty state when no codes provided',
    run: async () => {
      const Codebook = require('../../Components/Codebook/Codebook').default;
      return await renderComponent(
        withWrapper(
          <Codebook
            textCodes={[]}
            setUploadAudioModal={() => {}}
            handleUpdateCode={() => {}}
            handleAddNewCode={() => {}}
            t={mockT}
          />,
          '/project/p-test-1/codebook'
        ),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="codebook-container"]') !== null, 'codebook-container must render');
          assert(container.querySelector('[data-testid="codebook-empty-state"]') !== null, 'codebook-empty-state must render');
          assert(container.querySelector('[data-testid="codebook-empty-add-code-btn"]') !== null, 'empty-state add-code-btn must render');
          assert(container.querySelector('[data-testid="codebook-codes-list"]') === null, 'codes-list must NOT render in empty state');
        }
      );
    },
  },

  {
    id: 'codebook-with-codes',
    name: 'Codebook: renders code list and action buttons when codes exist',
    run: async () => {
      const Codebook = require('../../Components/Codebook/Codebook').default;
      return await renderComponent(
        withWrapper(
          <Codebook
            textCodes={[MOCK_CODE, MOCK_CODE_2]}
            setUploadAudioModal={() => {}}
            handleUpdateCode={() => {}}
            handleAddNewCode={() => {}}
            t={mockT}
          />,
          '/project/p-test-1/codebook'
        ),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="codebook-codes-list"]') !== null, 'codes-list must render');
          assert(container.querySelector('[data-testid="codebook-join-codes-btn"]') !== null, 'join-codes-btn must render');
          assert(container.querySelector('[data-testid="codebook-add-code-btn"]') !== null, 'add-code-btn must render');
          assert(container.querySelector('[data-testid="codebook-empty-state"]') === null, 'empty-state must NOT render when codes exist');
          assert(container.textContent.includes(MOCK_CODE.name), `must show "${MOCK_CODE.name}"`);
          assert(container.textContent.includes(MOCK_CODE_2.name), `must show "${MOCK_CODE_2.name}"`);
        }
      );
    },
  },

  {
    id: 'codebook-empty-add-code-opens-modal',
    name: 'Codebook: clicking empty-state Add code button opens TextCodeModal',
    run: async () => {
      const Codebook = require('../../Components/Codebook/Codebook').default;

      function Wrapper() {
        const [modal, setModal] = React.useState(null);
        return (
          <>
            <Codebook
              textCodes={[]}
              setUploadAudioModal={setModal}
              handleUpdateCode={() => {}}
              handleAddNewCode={() => {}}
              t={mockT}
            />
            {modal}
          </>
        );
      }

      const logCapture = createLogCapture();
      let snapshot = null;
      try {
        let result;
        await act(async () => { result = render(withWrapper(<Wrapper />, '/project/p-test-1/codebook')); });
        await act(async () => { await new Promise(r => setTimeout(r, 200)); });

        const addBtn = result.container.querySelector('[data-testid="codebook-empty-add-code-btn"]');
        assert(addBtn !== null, 'codebook-empty-add-code-btn must render before click');

        await act(async () => { fireEvent.click(addBtn); });
        await act(async () => { await new Promise(r => setTimeout(r, 200)); });

        assert(document.body.querySelector('[data-testid="text-code-modal"]') !== null, 'TextCodeModal must open after clicking Add code');
        snapshot = captureComponentSnapshot(result.container);
      } finally {
        cleanup();
        logCapture.restore();
      }
      return { snapshot, logs: logCapture.getLogs() };
    },
  },

  {
    id: 'codebook-join-codes-opens-modal',
    name: 'Codebook: clicking Join codes button opens TextCodeCombinationModal',
    run: async () => {
      const Codebook = require('../../Components/Codebook/Codebook').default;

      function Wrapper() {
        const [modal, setModal] = React.useState(null);
        return (
          <>
            <Codebook
              textCodes={[MOCK_CODE, MOCK_CODE_2]}
              setUploadAudioModal={setModal}
              handleUpdateCode={() => {}}
              handleAddNewCode={() => {}}
              t={mockT}
            />
            {modal}
          </>
        );
      }

      const logCapture = createLogCapture();
      let snapshot = null;
      try {
        let result;
        await act(async () => { result = render(withWrapper(<Wrapper />, '/project/p-test-1/codebook')); });
        await act(async () => { await new Promise(r => setTimeout(r, 200)); });

        const joinBtn = result.container.querySelector('[data-testid="codebook-join-codes-btn"]');
        assert(joinBtn !== null, 'codebook-join-codes-btn must render before click');

        await act(async () => { fireEvent.click(joinBtn); });
        await act(async () => { await new Promise(r => setTimeout(r, 200)); });

        assert(document.body.querySelector('[data-testid="text-code-combination-modal"]') !== null, 'TextCodeCombinationModal must open after clicking Join codes');
        snapshot = captureComponentSnapshot(result.container);
      } finally {
        cleanup();
        logCapture.restore();
      }
      return { snapshot, logs: logCapture.getLogs() };
    },
  },

  // ─── TextCodes panel ──────────────────────────────────────────────────────────

  {
    id: 'text-codes-panel-renders',
    name: 'TextCodes: renders panel with codes and create button',
    run: async () => {
      const TextCodes = require('../../Pages/Private/Settings/TextCoder/TextCodes').default;
      return await renderComponent(
        withWrapper(
          <TextCodes
            textCodes={[MOCK_CODE, MOCK_CODE_2]}
            setTextCodes={() => {}}
            handleSaveSelection={() => {}}
            createCodeModal={null}
            setCreateCodeModal={() => {}}
            pageActiveSelections={[MOCK_CODE.uuid, MOCK_CODE_2.uuid]}
            setPageActiveSelections={() => {}}
            isEditingTextCodes={false}
            setIsEditingTextCodes={() => {}}
            occurrencesByName={{}}
            setViewCodesModal={() => {}}
          />,
          '/project/p-test-1/text-coder'
        ),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="text-codes-panel"]') !== null, 'text-codes-panel must render');
          assert(container.querySelector('[data-testid="text-codes-create-btn"]') !== null, 'text-codes-create-btn must render');
          assert(container.textContent.includes(MOCK_CODE.name), `must show "${MOCK_CODE.name}"`);
          assert(container.textContent.includes(MOCK_CODE_2.name), `must show "${MOCK_CODE_2.name}"`);
        }
      );
    },
  },

  {
    id: 'text-codes-create-btn-opens-modal',
    name: 'TextCodes: clicking Create code opens TextCodeModal',
    run: async () => {
      const TextCodes = require('../../Pages/Private/Settings/TextCoder/TextCodes').default;

      function Wrapper() {
        const [modal, setModal] = React.useState(null);
        return (
          <TextCodes
            textCodes={[]}
            setTextCodes={() => {}}
            handleSaveSelection={() => {}}
            createCodeModal={modal}
            setCreateCodeModal={setModal}
            pageActiveSelections={[]}
            setPageActiveSelections={() => {}}
            isEditingTextCodes={false}
            setIsEditingTextCodes={() => {}}
            occurrencesByName={{}}
            setViewCodesModal={() => {}}
          />
        );
      }

      const logCapture = createLogCapture();
      let snapshot = null;
      try {
        let result;
        await act(async () => { result = render(withWrapper(<Wrapper />, '/project/p-test-1/text-coder')); });
        await act(async () => { await new Promise(r => setTimeout(r, 200)); });

        const createBtn = result.container.querySelector('[data-testid="text-codes-create-btn"]');
        assert(createBtn !== null, 'text-codes-create-btn must render before click');

        await act(async () => { fireEvent.click(createBtn); });
        await act(async () => { await new Promise(r => setTimeout(r, 200)); });

        assert(document.body.querySelector('[data-testid="text-code-modal"]') !== null, 'TextCodeModal must open after clicking Create code');
        snapshot = captureComponentSnapshot(result.container);
      } finally {
        cleanup();
        logCapture.restore();
      }
      return { snapshot, logs: logCapture.getLogs() };
    },
  },

  // ─── TextCoder page ───────────────────────────────────────────────────────────

  {
    id: 'text-coder-no-file-state',
    name: 'TextCoder page: renders no-file placeholder when no analysis is selected',
    run: async () => {
      const TextCoder = require('../../Pages/Private/Settings/TextCoder/TextCoder').default;
      const axios = require('axios');
      const originalAdapter = axios.defaults.adapter;
      axios.defaults.adapter = (config) => new Promise(resolve => setTimeout(() => resolve({
        data: [],
        status: 200, statusText: 'OK',
        headers: { 'content-type': 'application/json' },
        config, request: {},
      }), 30));

      const logCapture = createLogCapture();
      let snapshot = null;
      try {
        let result;
        await act(async () => {
          result = render(withWrapper(<TextCoder />, '/project/p-test-1/text-coder'));
        });
        await act(async () => { await new Promise(r => setTimeout(r, 500)); });

        const container = result.container;
        assert(container.querySelector('[data-testid="text-coder-container"]') !== null, 'text-coder-container must render');
        assert(container.querySelector('[data-testid="text-coder-no-file"]') !== null, 'text-coder-no-file must render when no analysis is selected');
        snapshot = captureComponentSnapshot(container);
      } finally {
        cleanup();
        axios.defaults.adapter = originalAdapter;
        logCapture.restore();
      }
      return { snapshot, logs: logCapture.getLogs() };
    },
  },

];
